import { useFlag } from '@afterpaytouch/feature'
import { useAppDispatch } from '../hooks'
import { setFeatureFlag } from './reducer'
import { Experiment, Experiments, KnownExperiment } from '../../model'
import { useExperiment } from '../experiments/hooks'
import { useFeatureFlagsState } from '..'

/**
 * Generic hook for cached feature flags.
 * First checks for cached value in Redux, if found returns it immediately.
 * If no cached value is found, calls LaunchDarkly and caches the result.
 *
 * @example
 * // Boolean flag (default behavior)
 * const isEnabled = useCachedFeatureFlag('my-boolean-flag')
 *
 * // String array flag
 * const sizes = useCachedFeatureFlag<string[]>('checkout-consistent-size', [])
 *
 * // String flag with default
 * const text = useCachedFeatureFlag<string>('maintenance-mode-text', 'Default text')
 *
 * // Number flag
 * const amount = useCachedFeatureFlag<number>('pay-in-2-amount', 0)
 *
 * @param key - The feature flag key
 * @param defaultValue - Optional default value if no cached value exists
 * @returns The feature flag value
 */
export const useCachedFeatureFlag = <T = boolean>(key: string, defaultValue?: T): T => {
  const dispatch = useAppDispatch()
  const { flags } = useFeatureFlagsState()
  const ldValue = useFlag(key, defaultValue as T)
  const cachedValue = flags[key] as T | undefined

  // If we have a valid cached value, return it immediately
  if (typeof cachedValue !== 'undefined') {
    return cachedValue
  }

  if (typeof ldValue !== 'undefined') {
    dispatch(setFeatureFlag({ key, value: ldValue }))
  }
  return ldValue
}

/**
 * Generic hook for cached experiment feature flags.
 * First checks for cached value in Redux, if found returns it.
 * If no cached value is found, calls the existing useExperiment hook and caches the result.
 * @param name - The experiment name
 * @param fireEvent - Whether to fire experiment events
 * @returns The experiment object
 */
export const useCachedExperiment = (name: KnownExperiment, fireEvent: boolean = true): Experiment => {
  const dispatch = useAppDispatch()
  const { flags } = useFeatureFlagsState()
  const cachedValue = flags[Experiments[name]] as Experiment | undefined
  const experiment = useExperiment(name, fireEvent)

  // If we have a valid cached value, return it immediately
  if (typeof cachedValue !== 'undefined') {
    return cachedValue
  }

  // Cache the experiment result when it's not loading
  if (!experiment.loading) {
    dispatch(setFeatureFlag({ key: Experiments[name], value: experiment }))
  }

  return experiment
}
